import React, { SFC } from 'react'
import { useTranslation } from 'react-i18next'
import { LocationInfo } from '../../../shared/types/location'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, InputAdornment, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import { Image } from '../../../shared/components/Image'
import { Link } from 'react-router-dom'
import SearchIcon from '@material-ui/icons/Search'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    imageWrapper: {
        flex: '1 0 auto',
        textAlign: 'right',
        marginLeft: theme.spacing(1),
    },
    image: {
        width: '80px',
        height: '80px',
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px'
    },
    dataInfoWrapper: {
        textAlign: 'center',
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface LocationListProps {
    locations: LocationInfo[]
    search: string
    onSearchChange(value: string): void
    selectedLocationId?: string
    onLocationSelected(location: LocationInfo): void
}

const LocationList: SFC<LocationListProps> = ({ 
    locations, 
    search, 
    onSearchChange, 
    selectedLocationId, 
    onLocationSelected 
}) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const locationsState = useSelector((state: any) => state.locations.items)
    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value)
    }

    return (
        <>
            <Grid container>
                <Grid item xs={10} className={classes.search}>
                    <TextField
                        fullWidth
                        variant="filled"
                        placeholder={t('locations.search')}
                        value={search}
                        onChange={handleSearchChanged}
                        className={classes.input}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={2} className={classes.progress}>
                    {!locationsState.executed && <CircularProgress size={30} />}
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {locations.map((location: LocationInfo) => (
                        <Card key={location.id} className={classes.listItem} elevation={1}>
                            <ListItem
                                button
                                selected={location.id === selectedLocationId}
                                onClick={() => onLocationSelected(location)}
                                className={classes.listItemButton}
                            >
                                <div className={classes.listContentWrapper}>
                                    <CardContent className={classes.listContent}>
                                        <Typography variant="h6">{location.name}</Typography>
                                        <Typography component="div" variant="caption">
                                            {t('locations.chargersCount', {
                                                countTotal: location.connectorCount,
                                                countWorking: location.workingConnectors,
                                                countInUse: location.availableConnectors,
                                            })}
                                        </Typography>
                                        <Typography component="div" variant="caption">
                                            <Link to="/history">
                                                {t('locations.chargesToday', {
                                                    count: location.chargingSessionsCount ?? 0,
                                                })}
                                                {location.chargingSessionsInProgressCount !== 0 && (
                                                    <span>
                                                        {t('locations.chargesInProgress', {
                                                            count: location.chargingSessionsInProgressCount ?? 0,
                                                        })}
                                                    </span>
                                                )}
                                            </Link>
                                        </Typography>
                                    </CardContent>
                                </div>
                                <div className={classes.imageWrapper}>
                                    <Image
                                        className={classes.image}
                                        imageClassName={classes.image}
                                        url={location.coverImage ? location.coverImage.url : undefined}
                                    />
                                </div>
                            </ListItem>
                        </Card>
                    ))}

                    {locationsState.executed && locations.length === 0 && (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('locations.noLocations')} />
                        </div>
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default LocationList